import React, { useState, useEffect } from "react";
import axios from "../../api/axios";
import { toast } from "react-toastify";
import { 
  Button,
  Col,
  Form,
  Row,
  Table,
  Spinner,
  Pagination,
  Badge,
  InputGroup,
  Modal
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import { Search, Printer, File, FileExcel, FileEarmarkExcel, FileEaselFill, FileExcelFill, Download, FileEarmarkSpreadsheet, ArrowLeft } from 'react-bootstrap-icons';
import Header from '../../layouts/Header';
import { useNavigate } from "react-router-dom";
import OutletSelect from "../../components/OutletSelect";

const MonthSummaryReport = () => {

  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);
  const navigate = useNavigate();
  
  const accessToken = localStorage.getItem("authToken");
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [selectedOutletName, setSelectedOutletName] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [subBtnLoading, setSubBtnLoading] = useState(false);
  const [printBtnLoading, setPrintBtnLoading] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [monthReportList, setMonthReportList] = useState([]);
  const [outletName, setOutletName] = useState('');
  const [clicked, setClicked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);
  
  // New state for dynamic tax percentages
  const [taxPercentages, setTaxPercentages] = useState([]);

  useEffect(() => {
    getOutlets();
  }, []);

  const handleDownloadExcel = async () => {
    setDownloadBtnLoading(true);
    getMonthReport(selectedMonth, selectedOutlet, currentPage, recordsPerPage, true);
  };

  const getMonthReport = async (monthDate, outlet, page = 1, limit = recordsPerPage, download) => {
    setLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        "content-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      };
      
      const month = monthDate.getMonth() + 1;
      const year = monthDate.getFullYear();

      const allMonths = [
        "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"
      ]

      let newUrl = `order/monthlyOrderSummary?outletId=${outlet.value}&year=${year}&month=${month}&page=${page}&limit=${limit}`

      if(download) {
        newUrl += `&isDownload=${download}`
      }
      const response = await axios.get(newUrl,
        { 
          headers, 
          responseType: download ? 'blob' : 'json'
        }
      );

      if (download) {
        // Download logic remains the same...
        const blob = new Blob([response.data], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        
        const fileName = `[${outlet?.label}] Summary Report ${allMonths[month - 1]}.xlsx`;
        link.download = fileName;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);

        toast.success('Excel file downloaded successfully!', {
            position: "top-right",
            autoClose: 3000,
            theme: "light"
        });
      }

      if (response?.data?.status === true && !download) {
        setMonthReportList(response?.data?.data?.reportData);
        setTotalPages(response?.data?.data?.pagination?.pages || 1);
        
        // Extract unique tax percentages from the response
        const allTaxPercentages = extractTaxPercentages(response?.data?.data?.reportData);
        setTaxPercentages(allTaxPercentages);
        
        toast.success("Data Fetched Successfully", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
      setDownloadBtnLoading(false);
    }
  };

  // Function to extract unique tax percentages from the report data
  const extractTaxPercentages = (reportData) => {
    if (!reportData || !Array.isArray(reportData)) return [];
    
    const percentages = new Set();
    
    reportData.forEach(report => {
      if (report?.taxDetails && Array.isArray(report.taxDetails)) {
        report.taxDetails.forEach(tax => {
          if (tax.taxPercent) {
            percentages.add(tax.taxPercent);
          }
        });
      }
    });
    
    // Convert Set to array and sort
    return Array.from(percentages).sort((a, b) => a - b);
  };

  // Function to get CGST/SGST percentage for a given tax percentage
  const getGSTPercentage = (taxPercent) => {
    return (taxPercent / 2).toFixed(1);
  };

  // Function to render dynamic tax column headers
  const renderTaxColumnHeaders = () => {
    return taxPercentages.map(percent => (
      <React.Fragment key={percent}>
        <th className="text-end">TAXABLE ({percent}%)</th>
        <th className="text-end">CGST ({getGSTPercentage(percent)}%)</th>
        <th className="text-end">SGST ({getGSTPercentage(percent)}%)</th>
      </React.Fragment>
    ));
  };

  // Function to render tax data for each row
  const renderTaxData = (report) => {
    return taxPercentages.map(percent => {
      const taxForPercent = report?.taxDetails?.filter(t => t.taxPercent === percent) || [];
      const totalTaxable = taxForPercent.reduce((acc, curr) => acc + (curr?.taxable || 0), 0);
      const totalCGST = taxForPercent.reduce((acc, curr) => acc + (curr?.CGST || 0), 0);
      const totalSGST = taxForPercent.reduce((acc, curr) => acc + (curr?.SGST || 0), 0);
      
      return (
        <React.Fragment key={percent}>
          <td className="text-end">₹ {totalTaxable.toFixed(2)}</td>
          <td className="text-end">₹ {totalCGST.toFixed(2)}</td>
          <td className="text-end">₹ {totalSGST.toFixed(2)}</td>
        </React.Fragment>
      );
    });
  };

  // Function to render grand total tax data
  const renderGrandTotalTaxData = () => {
    return taxPercentages.map(percent => {
      const totalTaxable = monthReportList.reduce((acc, report) => 
        acc + (report?.taxDetails?.filter(t => t.taxPercent === percent)
          .reduce((sum, tax) => sum + (tax.taxable || 0), 0) || 0), 0);
      
      const totalCGST = monthReportList.reduce((acc, report) => 
        acc + (report?.taxDetails?.filter(t => t.taxPercent === percent)
          .reduce((sum, tax) => sum + (tax.CGST || 0), 0) || 0), 0);
      
      const totalSGST = monthReportList.reduce((acc, report) => 
        acc + (report?.taxDetails?.filter(t => t.taxPercent === percent)
          .reduce((sum, tax) => sum + (tax.SGST || 0), 0) || 0), 0);
      
      return (
        <React.Fragment key={percent}>
          <td className="text-end fw-bold">₹ {totalTaxable.toFixed(2)}</td>
          <td className="text-end fw-bold">₹ {totalCGST.toFixed(2)}</td>
          <td className="text-end fw-bold">₹ {totalSGST.toFixed(2)}</td>
        </React.Fragment>
      );
    });
  };

  // Update the print function to handle dynamic tax columns
  const handlePrintReport = () => {
    // Calculate totals for print
    let totalInv = 0;
    let totalCancelledInv = 0;
    let totalDiscountAmt = 0;
    let grandTotalCancelledAmount = 0;
    let grandTotalTaxable = 0;
    let grandTotal = 0;

    // Calculate tax totals for print
    const taxTotals = {};
    taxPercentages.forEach(percent => {
      taxTotals[percent] = {
        taxable: 0,
        cgst: 0,
        sgst: 0
      };
    });

    const rowsHtml = monthReportList?.length > 0
      ? monthReportList.map((report) => {
          totalInv += report?.numberOfInvoices || 0;
          totalCancelledInv += report?.cancelledOrderCount || 0;
          totalDiscountAmt += report?.totalDiscount || 0;
          grandTotalCancelledAmount += report?.cancelledOrderAmount || 0;
          grandTotalTaxable += report?.totalTaxable || 0;
          grandTotal += report?.grandTotal || 0;

          // Calculate tax amounts for this row
          const rowTaxHtml = taxPercentages.map(percent => {
            const taxForPercent = report?.taxDetails?.filter(t => t.taxPercent === percent) || [];
            const taxable = taxForPercent.reduce((acc, curr) => acc + (curr?.taxable || 0), 0);
            const cgst = taxForPercent.reduce((acc, curr) => acc + (curr?.CGST || 0), 0);
            const sgst = taxForPercent.reduce((acc, curr) => acc + (curr?.SGST || 0), 0);
            
            // Add to totals
            taxTotals[percent].taxable += taxable;
            taxTotals[percent].cgst += cgst;
            taxTotals[percent].sgst += sgst;
            
            return `
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${taxable.toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${cgst.toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${sgst.toFixed(2)}</td>
            `;
          }).join("");

          return `
            <tr style="font-size: 11px">
              <td style="border: 1px solid #dee2e6; padding: 8px;">${report?.date}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px;">${report?.invoiceStartNo}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px;">${report?.invoiceEndNo}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center">${report?.numberOfInvoices}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center">${report?.cancelledOrderCount}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${report?.totalDiscount?.toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${report?.cancelledOrderAmount?.toFixed(2)}</td>
              ${rowTaxHtml}
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${report?.totalTaxable?.toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end">₹ ${report?.grandTotal?.toFixed(2)}</td>
            </tr>
          `;
        }).join("")
      : `<tr><td colspan="${13 + (taxPercentages.length * 3)}" style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">No Reports Found</td></tr>`;

    // Generate tax headers for print
    const taxHeadersHtml = taxPercentages.map(percent => `
      <th style="border: 1px solid #dee2e6; padding: 8px; text-align: end">TAXABLE (${percent}%)</th>
      <th style="border: 1px solid #dee2e6; padding: 8px; text-align: end">CGST (${getGSTPercentage(percent)}%)</th>
      <th style="border: 1px solid #dee2e6; padding: 8px; text-align: end">SGST (${getGSTPercentage(percent)}%)</th>
    `).join("");

    // Generate tax totals for print
    const taxTotalsHtml = taxPercentages.map(percent => `
      <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end; font-weight: bold">₹ ${taxTotals[percent].taxable.toFixed(2)}</td>
      <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end; font-weight: bold">₹ ${taxTotals[percent].cgst.toFixed(2)}</td>
      <td style="border: 1px solid #dee2e6; padding: 8px; text-align: end; font-weight: bold">₹ ${taxTotals[percent].sgst.toFixed(2)}</td>
    `).join("");

    const formattedMonth = selectedMonth.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });

    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>Monthly Summary Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .title { font-size: 18px; font-weight: bold; }
            .date-range { font-size: 14px; color: #666; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th { background-color: #f8f9fa; text-align: left; border: 1px solid #dee2e6; padding: 8px; }
            td { border: 1px solid #dee2e6; padding: 8px; }
            .text-center { text-align: center; }
            .text-end { text-align: right; }
            .total-row { font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">Monthly Summary Report</div>
            <div class="date-range">${formattedMonth} - ${outletName}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>DATE</th>
                <th>INV. START NO</th>
                <th>INV. END NO</th>
                <th class="text-center">TOT. INV.</th>
                <th class="text-center">CANC. INV.</th>
                <th class="text-end">DIS. AMT</th>
                <th class="text-end">CANC. AMT</th>
                ${taxHeadersHtml}
                <th class="text-end">TOT. TAXABLE</th>
                <th class="text-end">GRAND TOTAL</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
              <tr class="total-row">
                <td colspan="3" style="text-align: right;">GRAND TOTAL:</td>
                <td class="text-center">${totalInv}</td>
                <td class="text-center">${totalCancelledInv}</td>
                <td class="text-end">₹ ${totalDiscountAmt.toFixed(2)}</td>
                <td class="text-end">₹ ${grandTotalCancelledAmount.toFixed(2)}</td>
                ${taxTotalsHtml}
                <td class="text-end">₹ ${grandTotalTaxable.toFixed(2)}</td>
                <td class="text-end">₹ ${grandTotal.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
          <script>
            setTimeout(() => {
              window.print();
              window.close();
            }, 500);
          </script>
        </body>
      </html>
    `);
    win.document.close();
  };

  // ... rest of your existing functions (getOutlets, handleApiError, handleSubmit, getOutletName, handlePageChange, renderPaginationItems) remain the same

  const getOutlets = async () => {
    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`,
      };
      const response = await axios.get("outlet", { headers });

      if (response?.data?.status === true) {
        const activeOutlets = response?.data?.data?.outlets.filter(
          (active) => active?.status === "ACTIVE" || "INACTIVE"
        );
        setOutlets(activeOutlets);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleApiError = (error) => {
    if (error?.response) {
      let errorMessage = "";
      if (error.response?.status === 406) {
        errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.error;
      } else if (error.response?.status === 412) {
        errorMessage = error?.response?.data?.data?.message;
      } else if (error.response?.status === 500) {
        errorMessage = error?.response?.data?.error;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubBtnLoading(true);
    setClicked(true);
    setCurrentPage(1);
    await getMonthReport(selectedMonth, selectedOutlet);
    setSubBtnLoading(false);
  };

  const getOutletName = (e) => {   
    const value = outlets?.filter(val => val._id === e);
    setOutletName(value[0]?.name || '');  
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      getMonthReport(selectedMonth, selectedOutlet, page, recordsPerPage);
    }
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage, endPage;

    if (totalPages <= maxVisiblePages) {
      startPage = 1;
      endPage = totalPages;
    } else {
      const maxVisibleBeforeCurrent = Math.floor(maxVisiblePages / 2);
      const maxVisibleAfterCurrent = Math.ceil(maxVisiblePages / 2) - 1;
      
      if (currentPage <= maxVisibleBeforeCurrent) {
        startPage = 1;
        endPage = maxVisiblePages;
      } else if (currentPage + maxVisibleAfterCurrent >= totalPages) {
        startPage = totalPages - maxVisiblePages + 1;
        endPage = totalPages;
      } else {
        startPage = currentPage - maxVisibleBeforeCurrent;
        endPage = currentPage + maxVisibleAfterCurrent;
      }
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" />);
      }
      items.push(
        <Pagination.Item 
          key={totalPages} 
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  const formatMonth = selectedMonth
    .toLocaleString("en-US", { month: "long", year: "numeric" })
    .toUpperCase();

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
            <div>
                <h4 className="mb-0 fw-bold d-inline-block">
                    <i className="bi bi-calendar-month me-2"></i>MONTHLY SUMMARY REPORT
                </h4>
            </div>
            <div>
                <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={() => navigate('/reports/all-reports')}
                    className="me-3 d-flex align-items-center fw-semibold"
                    style={{ height: '38px' }}
                >
                    <ArrowLeft className="me-1" /> Back to Reports
                </Button>
            </div>
          </div>
          <hr className='mt-0 mb-0' /> 
          
          <div className="card-body">
            {/* Filters Section */}
            <div className="p-3 bg-light rounded-3">
              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group controlId="outletSelect">
                      <OutletSelect
                        value={selectedOutlet}
                        onChange={(option) => setSelectedOutlet(option)}
                        required={true}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group controlId="monthSelect">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-calendar me-2"></i>Select Month
                      </Form.Label>
                      <DatePicker
                        selected={selectedMonth}
                        onChange={(date) => setSelectedMonth(date)}
                        dateFormat="MMM yyyy"
                        showMonthYearPicker
                        className="form-control border-secondary"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3} className="d-flex align-items-end">
                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={!selectedOutlet || !selectedMonth}
                    >
                      {subBtnLoading ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        "Generate Report"
                      )}
                    </Button>
                  </Col>

                  <Col className="d-flex align-items-end">
                      <Button
                        variant="outline-success"
                        className="me-3 w-100 fw-semibold"
                        onClick={handleDownloadExcel}
                        disabled={!monthReportList.length}
                      >
                        <FileEarmarkSpreadsheet className="me-2" />
                        { downloadBtnLoading ? <Spinner size="sm" /> : 'Download Excel' }
                      </Button>
                  </Col>
                </Row>
              </Form>
            </div>

            {/* Results Section */}
            {!clicked ? (
              <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3">
                <div className="text-center">
                  <i className="bi bi-clipboard-data fs-1 text-muted"></i>
                  <h5 className="text-muted mt-3">
                    Please select an outlet and month to view the monthly summary
                  </h5>
                </div>
              </div>
            ) : (
              <>
                {/* Pagination and Records Per Page - Top */}
                <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                    <div className="d-flex align-items-center">
                        <span className="me-2 text-muted">Show</span>
                        <Form.Select
                          style={{ width: 'auto' }}
                          value={recordsPerPage}
                          onChange={(e) => {
                              setRecordsPerPage(Number(e.target.value));
                              setCurrentPage(1);
                              getMonthReport(selectedMonth, selectedOutlet, 1, Number(e.target.value));
                          }}
                          className="border-secondary"
                        >
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={200}>200</option>
                          <option value={300}>300</option>
                          <option value={500}>500</option>
                        </Form.Select>
                        <span className="ms-2 text-muted">entries</span>
                    </div>
                  
                    <div className="d-flex align-items-center">
                        <Pagination className="mb-0">
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />
                            {renderPaginationItems()}
                            <Pagination.Next
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                        </Pagination>
                  </div>
                </div>

                {/* Monthly Summary Table */}
                <div className="table-responsive border rounded-3">
                  <Table striped bordered hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>DATE</th>
                        <th>INV. START NO</th>
                        <th>INV. END NO</th>
                        <th className="text-center">TOT. INV.</th>
                        <th className="text-center">CANC. INV.</th>
                        <th className="text-end">DIS. AMT</th>
                        <th className="text-end">CANC. AMT</th>
                        
                        {/* Dynamic Tax Column Headers */}
                        {renderTaxColumnHeaders()}
                        
                        <th className="text-end">TOT. TAXABLE</th>
                        <th className="text-end">GRAND TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={9 + (taxPercentages.length * 3)} className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 mb-0">Loading monthly summary...</p>
                          </td>
                        </tr>
                      ) : monthReportList?.length > 0 ? (
                        <>
                          {monthReportList.map((report, index) => (
                            <tr key={index}>
                              <td>{report?.date}</td>
                              <td>{report?.invoiceStartNo}</td>
                              <td>{report?.invoiceEndNo}</td>
                              <td className="text-center">{report?.numberOfInvoices}</td>
                              <td className="text-center text-danger">{report?.cancelledOrderCount}</td>
                              <td className="text-end">₹ {report?.totalDiscount?.toFixed(2)}</td>
                              <td className="text-end text-danger">₹ {report?.cancelledOrderAmount?.toFixed(2)}</td>
                              
                              {/* Dynamic Tax Data */}
                              {renderTaxData(report)}
                              
                              <td className="text-end">₹ {report?.totalTaxable?.toFixed(2)}</td>
                              <td className="text-end fw-semibold text-success">
                                ₹ {report?.grandTotal?.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-light">
                            <td colSpan="3" className="text-end fw-bold">GRAND TOTAL:</td>
                            <td className="text-center fw-bold">
                              {monthReportList.reduce((acc, report) => acc + (report?.numberOfInvoices || 0), 0)}
                            </td>
                            <td className="text-center fw-bold text-danger">
                              {monthReportList.reduce((acc, report) => acc + (report?.cancelledOrderCount || 0), 0)}
                            </td>
                            <td className="text-end fw-bold">
                              ₹ {monthReportList.reduce((acc, report) => acc + (report?.totalDiscount || 0), 0).toFixed(2)}
                            </td>
                            <td className="text-end fw-bold text-danger">
                              ₹ {monthReportList.reduce((acc, report) => acc + (report?.cancelledOrderAmount || 0), 0).toFixed(2)}
                            </td>
                            
                            {/* Dynamic Grand Total Tax Data */}
                            {renderGrandTotalTaxData()}
                            
                            <td className="text-end fw-bold">
                              ₹ {monthReportList.reduce((acc, report) => acc + (report?.totalTaxable || 0), 0).toFixed(2)}
                            </td>
                            <td className="text-end fw-bold text-success">
                              ₹ {monthReportList.reduce((acc, report) => acc + (report?.grandTotal || 0), 0).toFixed(2)}
                            </td>
                          </tr>
                        </>
                      ) : (
                        <tr>
                          <td colSpan={8 + (taxPercentages.length * 3)} className="text-center py-4">
                            <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                            <p className="mt-2 mb-0 text-muted">
                              No data found for the selected month and outlet
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination and Records Per Page - Bottom */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    Showing {(currentPage - 1) * recordsPerPage + 1} to{' '}
                    {Math.min(currentPage * recordsPerPage, monthReportList?.length)} of{' '}
                    {monthReportList?.length} entries
                  </div>
                  <Pagination>
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default MonthSummaryReport;